import { BreadcrumbItem } from '@/components/Breadcrumb';
import { INDEXABLE_DEEP_JOB_LISTING_PATHS } from '@/constants/indexableJobListingFacets';
import { ALL_LOCATIONS } from '@/constants/jobLocations';
import { IQuery } from '@/types/alerts';
import { parseAndFormatDate } from '@/utils/dateHelper';
import {
  convertLocationStringToLocationDisplayList,
  convertLocationsToValues,
  generateLocationNameFromLocationString,
} from '@/utils/jobLocationsHelper';
import { textlengthLimit } from '@/utils/limitTextLength';
import axios from 'axios';
import {
  ALL_AUSTRALIA,
  ALL_NEW_ZEALAND,
  LOCATIONS,
  stateAbbreviations,
} from 'constants/jobLocations';
import { PATH } from 'constants/path';
import dayjs from 'dayjs';
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';
import { slug } from 'to-case';
import {
  IGrade,
  IJobDetail,
  IJobSpeciality,
  ISpeciality,
  JobGrade,
  JobListPaginatedResults,
  JobSearchResultDetail,
  JobSearchWebCriteria,
} from 'types/jobs';
import { User } from 'types/user';
import { UrlObject } from 'url';

import { appServerConfig } from './appServerConfig';

export const ALL_AUSTRALIA_LOCATION_VALUES = LOCATIONS[ALL_AUSTRALIA].map(item => item.value).join(
  ';',
);
export const ALL_NEW_ZEALAND_LOCATION_VALUES = LOCATIONS[ALL_NEW_ZEALAND].map(
  item => item.value,
).join(';');

const WORK_TYPE_SLUG_MAP = {
  locum: 'Locum',
  permanent: 'Permanent',
} as const;

type WorkTypeSlug = keyof typeof WORK_TYPE_SLUG_MAP;

const locationQueryValuesBySlug = {
  australia: ALL_AUSTRALIA_LOCATION_VALUES,
  'new-zealand': ALL_NEW_ZEALAND_LOCATION_VALUES,
  sydney: 'New South Wales',
  melbourne: 'Victoria',
  brisbane: 'Queensland',
  perth: 'Western Australia',
  adelaide: 'South Australia',
  auckland: 'Auckland',
  wellington: 'Wellington',
  christchurch: 'Christchurch',
} as const;

const locationNameBySlug = {
  australia: 'Australia',
  'new-zealand': 'New Zealand',
} as const;

const canonicalCitySlugs = new Set([
  'sydney',
  'melbourne',
  'brisbane',
  'perth',
  'adelaide',
  'auckland',
  'wellington',
  'christchurch',
]);

export const isCanonicalCitySlug = (value: string) => canonicalCitySlugs.has(value);

// Grade slugs the listing page's own segment parser recognizes as the grade segment.
export const KNOWN_GRADE_SLUGS = new Set([
  'medical-student',
  'resident-medical-officer',
  'registrar',
  'smo-moss',
  'specialist-consultant',
  'clinical-director',
  'director-of-medical-services',
]);

// Baseline speciality slugs used as a fallback and for canonical root categories - shared
// with the listing page's own segment classification so the sitemap never emits a URL the
// page itself would misclassify (e.g. a subspeciality slug mistaken for a location).
export const BASE_KNOWN_SPECIALITY_SLUGS = new Set([
  'anaesthetics',
  'emergency-medicine-ed',
  'general-practice-gp',
  'intensive-care-medicine',
  'medical-administration',
  'medicine',
  'obstetrics-and-gynaecology',
  'ophthalmology',
  'paediatrics',
  'pathology',
  'psychiatry',
  'radiation-oncology',
  'radiology',
  'rural-hospital-medicine',
  'surgery',
  'urgent-care',
]);

export const createKnownSpecialitySlugs = (jobSpecialities: IJobSpeciality[] = []) => {
  const cmsSpecialitySlugs = jobSpecialities
    .map(speciality => toCanonicalSlug(speciality.name))
    .filter(Boolean);

  return new Set<string>([...BASE_KNOWN_SPECIALITY_SLUGS, ...cmsSpecialitySlugs]);
};

const normalizeLocationValueList = (location: string) =>
  location
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .sort()
    .join(';');

export const toCanonicalSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

// NZ's regions ("North Island"/"South Island") are broad areas like AU states, not real-world
// cities to geocode - without these, shouldResolveCoordinatesForLocation treats them as unknown
// slugs and geocodes them, silently shrinking a region-wide filter down to a 50km circle around
// one geocoded point.
const NZ_REGION_SLUGS = LOCATIONS[ALL_NEW_ZEALAND].map(location => toCanonicalSlug(location.label));

// The canonical location slugs recognized by the listing page's own segment parser.
export const KNOWN_CANONICAL_LOCATION_SLUGS = new Set<string>([
  ...Object.keys(locationNameBySlug),
  ...Object.keys(stateAbbreviations).map(toCanonicalSlug),
  ...NZ_REGION_SLUGS,
  ...canonicalCitySlugs,
]);

const toDisplayValueFromSlug = (value: string) =>
  value
    .split('-')
    .filter(Boolean)
    .map(token => `${token.charAt(0).toUpperCase()}${token.slice(1)}`)
    .join(' ');

export const generateCanonicalLocationSlug = (location: string) => {
  // location may hold display labels (e.g. 'North Island') rather than the
  // internal values (e.g. 'NZ North Island') the ALL_* group constants use.
  const normalizedLocationValues = normalizeLocationValueList(convertLocationsToValues(location));

  if (normalizedLocationValues === normalizeLocationValueList(ALL_AUSTRALIA_LOCATION_VALUES)) {
    return 'australia';
  }

  if (normalizedLocationValues === normalizeLocationValueList(ALL_NEW_ZEALAND_LOCATION_VALUES)) {
    return 'new-zealand';
  }

  if (location.includes(';')) {
    return undefined;
  }

  // A single value may still be an internal NZ region value (e.g. 'NZ North Island') rather
  // than its display label ('North Island') - slugify the resolved display name so the path
  // segment matches NZ_REGION_SLUGS/KNOWN_CANONICAL_LOCATION_SLUGS and round-trips back to the
  // correct value via getLocationQueryValueFromCanonicalSlug.
  return toCanonicalSlug(generateLocationNameFromLocationString(location) || location);
};

export const isCanonicalWorkTypeSlug = (value: string) => value in WORK_TYPE_SLUG_MAP;

export const toCanonicalWorkTypeSlug = (workType?: string) => {
  if (!workType) {
    return undefined;
  }

  const slugValue = toCanonicalSlug(workType) as WorkTypeSlug;
  return isCanonicalWorkTypeSlug(slugValue) ? slugValue : undefined;
};

export const getWorkTypeFromCanonicalSlug = (workTypeSlug: string) => {
  if (!isCanonicalWorkTypeSlug(workTypeSlug)) {
    return undefined;
  }

  return WORK_TYPE_SLUG_MAP[workTypeSlug as WorkTypeSlug];
};

export const getLocationQueryValueFromCanonicalSlug = (locationSlug: string) =>
  locationQueryValuesBySlug[locationSlug as keyof typeof locationQueryValuesBySlug] ||
  toDisplayValueFromSlug(locationSlug);

export const getLocationNameFromCanonicalSlug = (locationSlug: string) =>
  locationNameBySlug[locationSlug as keyof typeof locationNameBySlug] ||
  toDisplayValueFromSlug(locationSlug);

type CanonicalListingFilters = {
  grade?: string;
  speciality?: string;
  location?: string;
  workType?: string;
};

export const buildCanonicalJobListingPath = ({
  grade,
  speciality,
  location,
  workType,
}: CanonicalListingFilters) => {
  const locationSlug =
    typeof location === 'string' && location ? generateCanonicalLocationSlug(location) : undefined;
  const workTypeSlug = toCanonicalWorkTypeSlug(workType);

  const parts = [grade, speciality, locationSlug, workTypeSlug]
    .filter((part): part is string => Boolean(part))
    .map(toCanonicalSlug);

  return `${PATH.JOBSLISTSEO}${parts.length ? `/${parts.join('/')}` : ''}`;
};

export const buildCanonicalJobListingUrl = (filters: CanonicalListingFilters, origin: string) => {
  const normalizedOrigin = origin.replace(/\/+$/, '');
  return `${normalizedOrigin}${buildCanonicalJobListingPath(filters)}`;
};

export type JobListingIndexingFilters = CanonicalListingFilters & {
  currentPage: number;
  hasResults: boolean;
};

/**
 * A2P-4452: whether a job listing page should be served noindex,follow. Indexable pages are
 * page 1 of a facet with live results, at grade/speciality depth or shallower - the location and
 * work-type layers below that are only indexable when curated into
 * INDEXABLE_DEEP_JOB_LISTING_PATHS, since the facet engine generates thousands of mostly empty
 * or near-duplicate combinations there.
 */
export const shouldNoIndexJobListing = ({
  currentPage,
  hasResults,
  ...filters
}: JobListingIndexingFilters) => {
  if (currentPage > 1 || !hasResults) {
    return true;
  }

  const isDeepFacet = Boolean(filters.location || filters.workType);
  return (
    isDeepFacet && !INDEXABLE_DEEP_JOB_LISTING_PATHS.has(buildCanonicalJobListingPath(filters))
  );
};

const toGradeSpecialityKey = (grade: string, speciality?: string) =>
  [grade, speciality].filter(Boolean).map(toCanonicalSlug).join('/');

/**
 * Generates the job listing urls for sitemap in the SEO friendly way - grade and grade +
 * speciality pages, plus the curated deep facets in INDEXABLE_DEEP_JOB_LISTING_PATHS. Any URL
 * the listing page would serve noindex (see shouldNoIndexJobListing) must stay out of here.
 * @param liveJobs the site's complete current job pool (fetched by the caller - this module must
 * stay free of `services/*` imports, since it's also bundled client-side by JobListing
 * components), used to leave out grade/speciality facets with no live jobs - so an empty pool
 * yields no job listing URLs at all. Pass `undefined` when the pool is unavailable or
 * incomplete: nothing is filtered then, rather than dropping facets that may well have jobs.
 * @returns array of strings - urls
 * @throws error if exception - calling method to handle it
 */
export const generateJobListingUrlsForSitemap = async (
  liveJobs?: SimilarSearchJobSummary[],
): Promise<Array<string>> => {
  const api = axios.create({
    baseURL: appServerConfig.recruitApiUrl,
  });

  const liveFacetKeys = liveJobs && new Set<string>();
  liveJobs?.forEach(({ grade, speciality }) => {
    if (grade) {
      liveFacetKeys?.add(toGradeSpecialityKey(grade));
      liveFacetKeys?.add(toGradeSpecialityKey(grade, speciality));
    }
  });
  const hasLiveJobs = (grade: string, speciality?: string) =>
    !liveFacetKeys || liveFacetKeys.has(toGradeSpecialityKey(grade, speciality));

  let { data: grades }: { data: Array<IGrade> } = await api.get('/v1/picklist/grades');

  grades = grades.filter(item => item.key !== 'Medical Student');

  const gradeSpecialities: { grade: JobGrade; specialties: ISpeciality[] }[] = await Promise.all(
    grades.map(async grade => {
      const { data: specialties }: { data: Array<ISpeciality> } = await api.get(
        `/v1/picklist/${encodeURIComponent(grade.key)}/specialities`,
      );
      return {
        grade: grade.key,
        specialties: specialties,
      };
    }),
  );

  const jobListingUrls: string[] = [];

  for (const { grade, specialties } of gradeSpecialities) {
    if (hasLiveJobs(grade)) {
      jobListingUrls.push(buildCanonicalJobListingPath({ grade }));
    }

    for (const speciality of specialties) {
      if (hasLiveJobs(grade, speciality.key)) {
        jobListingUrls.push(buildCanonicalJobListingPath({ grade, speciality: speciality.key }));
      }
    }
  }

  // Deep facets are filtered on their grade/speciality parent having live jobs - the pool can't
  // cheaply resolve the listing page's location matching (state/city radius), so an allowlisted
  // facet can still be empty for a while; the page itself noindexes it in that case.
  INDEXABLE_DEEP_JOB_LISTING_PATHS.forEach(path => {
    const [grade, speciality] = path.slice(`${PATH.JOBSLISTSEO}/`.length).split('/');
    if (hasLiveJobs(grade, speciality)) {
      jobListingUrls.push(path);
    }
  });

  return jobListingUrls;
};

/**
 * Generates the jobs listing pathname
 *
 * @param grade
 * @param speciality
 * @param rootSpeciality root speciality which is used when feature flag is on
 * @param subspeciality sub speciality which is used when feature flag is on - generateJobListingsUrlObject
 * deliberately never passes this through (it keeps subspeciality in the query string instead, see
 * its own comment), but the parameter stays for direct callers/tests of this lower-level builder.
 * @returns string with format /jobs/listing/${grade}/${speciality}
 */
export const generateJobListingsURL = (
  grade?: string,
  speciality?: string,
  rootSpeciality?: string,
  subspeciality?: string,
  location?: string,
  workType?: string,
) => {
  let jobListingBaseUrl = `${PATH.JOBSLISTSEO}/`;

  if (grade) {
    jobListingBaseUrl += `${slug(grade.toLowerCase())}/`;
  }
  if (speciality) {
    jobListingBaseUrl += `${slug(speciality.toLowerCase())}/`;
  }
  if (rootSpeciality) {
    jobListingBaseUrl += `${slug(rootSpeciality.toLowerCase())}/`;
  }
  if (subspeciality) {
    jobListingBaseUrl += `${slug(subspeciality.toLowerCase())}/`;
  }
  if (location) {
    jobListingBaseUrl += `${slug(location.toLowerCase())}/`;
  }
  if (workType) {
    jobListingBaseUrl += `${slug(workType.toLowerCase())}/`;
  }

  // No path segment was added (e.g. a keyword/pay-rate/date-only search) - route to the bare
  // listing page (pages/jobs/listing/[[...listing]].tsx, an optional catch-all) rather than a
  // dangling trailing slash. Every search now lands on /jobs/listing regardless of which filters
  // are set, instead of falling back to the separate /jobs/list page.
  return jobListingBaseUrl === `${PATH.JOBSLISTSEO}/` ? PATH.JOBSLISTSEO : jobListingBaseUrl;

  // if (grade && speciality) {
  //   return `${PATH.JOBSLISTSEO}/${slug(grade.toLowerCase())}/${slug(speciality.toLowerCase())}`;
  // }

  // if (grade) {
  //   return `${PATH.JOBSLISTSEO}/${slug(grade.toLowerCase())}`;
  // }
};

//More details at https://dev.to/harry0000/a-bit-convenient-typescript-type-definitions-for-objectentries-d6g
type ValueOf<T> = T[keyof T];
type Entries<T> = [keyof T, ValueOf<T>][];

function objectEntries<T extends object>(obj: T): Entries<T> {
  return Object.entries(obj) as Entries<T>;
}
/**
 *  Remove params from the query string if they:
 *  - are falsy: undefined, null etc.
 *  - have 'none' as value: could come from form controls like grade, specialty, date
 *  - have 'listing' as key: could come from /jobs/listing/<grade>/<specialty> route as a param
 */
export function purgeQuery<T extends object>(query: T): T {
  const purgedQuery = { ...query };
  for (const [key, value] of objectEntries(query)) {
    if (!value || value === 'none' || key === 'listing') {
      delete purgedQuery[key];
    }
  }
  return purgedQuery;
}

/**
 * Generates the jobs listing url object
 *
 * @param query
 * @returns UrlObject
 */
export const generateJobListingsUrlObject = (
  query: JobSearchWebCriteria & { listing?: string },
): UrlObject => {
  const purgedQuery = purgeQuery<JobSearchWebCriteria>(query);

  const {
    speciality,
    grade,
    rootSpeciality,
    subspeciality,
    location,
    workType,
    locationName,
    ...rest
  } = purgedQuery;
  const newQuery: ParsedUrlQueryInput = { ...rest };
  // A city (lat/lon) selection has no 'location' string, only a locationName -
  // fall back to it so cities also get a canonical location path slug.
  const locationForCanonicalSlug =
    (typeof location === 'string' && location) ||
    (typeof locationName === 'string' && locationName) ||
    undefined;
  const canonicalLocationSlug = locationForCanonicalSlug
    ? generateCanonicalLocationSlug(locationForCanonicalSlug)
    : undefined;
  const canonicalWorkTypeSlug =
    typeof workType === 'string' ? toCanonicalWorkTypeSlug(workType) : undefined;
  // Location/workType are promoted into the path whenever they resolve to a clean slug on their
  // own - a Role filter (grade/speciality) is no longer required. generateCanonicalLocationSlug
  // already returns undefined for ambiguous multi-select locations, so those still fall back to
  // the query string below.
  const pathLocationSlug = canonicalLocationSlug;
  const pathWorkTypeSlug = canonicalWorkTypeSlug;

  // Skip locationName when the location is already encoded in the URL path slug
  // (canonical listing pages regenerate it from the path, see getGradeSpeciality).
  if (!pathLocationSlug) {
    if (locationName) {
      newQuery.locationName = locationName;
    } else {
      // generate locationName if missing
      const displayName = generateLocationNameFromLocationString(location);
      if (displayName) {
        newQuery.locationName = displayName;
      }
    }
  }

  if (!pathLocationSlug && location) {
    if (location) {
      newQuery.location = location;
    }
  }

  if (!pathWorkTypeSlug && workType) {
    newQuery.workType = workType;
  }

  // Subspeciality stays in the query string rather than being slugified into the path. The
  // listing page's segment parser can only recognize a second speciality path segment as a
  // subspeciality if it's in its known-specialities set, and enumerating every v2 subspeciality
  // (a grade x root-speciality fan-out) is expensive - a query param round-trips through
  // getServerSideProps for free (it's read straight off the query object) without that cost, and
  // without the segment ever being misread as a location.
  if (subspeciality) {
    newQuery.subspeciality = subspeciality;
  }

  return {
    pathname: generateJobListingsURL(
      grade,
      speciality,
      rootSpeciality,
      undefined,
      pathLocationSlug,
      pathWorkTypeSlug,
    ),
    query: newQuery,
  };
};

/**
 * Generates the root-relative jobs details URL. The result is in one of the following formats
 * /jobs/grade/speciality/jnxxxxxxxx
 * /jobs/grade/jnxxxxxxxx
 * /jobs/jnxxxxxxxx
 *
 * @param jobNumber the human friendly job reference number from Salesforce
 * @param grade the grade the job is for, if any
 * @param speciality the specialty the job is for, if any
 * @returns the canonical URL for rendering the job page
 */
export const generateJobDetailsURL = (jobNumber?: string, grade?: string, speciality?: string) => {
  if (grade && speciality && jobNumber) {
    return `${PATH.JOBS}/${slug(grade.toLowerCase())}/${slug(
      speciality.toLowerCase(),
    )}/${jobNumber.toLowerCase()}`;
  }

  if (grade && jobNumber) {
    return `${PATH.JOBS}/${slug(grade.toLowerCase())}/${jobNumber.toLowerCase()}`;
  }

  if (jobNumber) {
    return `${PATH.JOBS}/${jobNumber.toLowerCase()}`;
  }

  return '';
};

export const generateJobListTrackingDetails = (
  queryData: JobSearchWebCriteria,
  user?: User | null | undefined,
) => ({
  logged_in: user ? true : false,
  grade: queryData['grade'],
  speciality: queryData['speciality'],
  work_type: queryData['workType'],
  start_date: queryData['startDate'],
  end_date: queryData['endDate'],
  keywords: queryData['keywords'],
  location: queryData['location'],
});

export type JobListingPageHeaderContext = {
  grade?: string;
  speciality?: string;
  location?: string;
};

// getServerSideProps callers pass ctx.query straight through, typed as JobSearchWebCriteria for
// convenience - but a repeated query param (e.g. ?speciality=Surgery&speciality=Medicine) makes
// Next parse that key as a string[] at runtime regardless of the declared string type, which
// would otherwise reach prioritiseByContext's `value.trim()` and throw. Take only the first value
// in that case, matching how a single-select filter would normally behave.
export const toSingleQueryValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

/**
 * Derives the grade/speciality/location the job listing page is currently themed around, used
 * both for on-page copy (headings, breadcrumbs) and for ranking CMS content (testimonials, FAQs)
 * against the active search context. Kept as a single source of truth so getServerSideProps can
 * filter testimonials/FAQs down to the relevant subset server-side - shipping the whole CMS
 * collection to the client just to filter it down to a handful of cards bloats the page payload.
 */
export const derivePageHeaderContext = (
  query: JobSearchWebCriteria,
): JobListingPageHeaderContext => {
  const gradeValue = toSingleQueryValue(query.grade);
  const specialityValue = toSingleQueryValue(query.speciality);
  const rootSpecialityValue = toSingleQueryValue(query.rootSpeciality);
  const locationNameValue = toSingleQueryValue(query.locationName);
  const locationValue = toSingleQueryValue(query.location);

  const grade = gradeValue && gradeValue !== 'none' ? gradeValue : undefined;
  const speciality =
    (specialityValue !== 'none' && specialityValue) ||
    (rootSpecialityValue !== 'none' && rootSpecialityValue) ||
    undefined;
  const location =
    locationNameValue || generateLocationNameFromLocationString(locationValue) || undefined;

  return { grade, speciality, location };
};

export type ExploreSimilarSearchesLocationTier = 'none' | 'country' | 'state' | 'city';

export interface ExploreSimilarSearchesLocationContext {
  tier: ExploreSimilarSearchesLocationTier;
  // Set only when tier === 'country'
  country?: 'AU' | 'NZ';
  // Set when tier === 'state', and (best-effort) when tier === 'city' - the state a
  // geocoded city search resolved to server-side (see resolveCoordinatesForCity).
  state?: string;
  // Set only when tier === 'city' - the free-text place name the visitor searched for.
  city?: string;
}

/**
 * Classifies the active location filter into the tier the "Explore similar searches" section
 * should build its location cards around - a whole country, a single state/region, a geocoded
 * city search, or nothing at all. Reuses the same signals already used elsewhere on the page
 * (`query.lon && query.lat` for a city search, `query.state` for its resolved state) rather than
 * introducing new query params.
 */
export const deriveExploreSimilarSearchesLocationContext = (
  query: JobSearchWebCriteria,
): ExploreSimilarSearchesLocationContext => {
  const isCitySelected = !!(query.lon && query.lat);
  const stateValue = toSingleQueryValue(query.state);
  const state = stateValue && stateValue !== 'none' ? stateValue : undefined;

  if (isCitySelected) {
    const cityValue =
      toSingleQueryValue(query.locationName) ||
      generateLocationNameFromLocationString(toSingleQueryValue(query.location)) ||
      undefined;
    return { tier: 'city', city: cityValue, state };
  }

  const locationValue = toSingleQueryValue(query.location);
  if (!locationValue) {
    return { tier: 'none' };
  }

  const normalizedLocationValues = normalizeLocationValueList(
    convertLocationsToValues(locationValue),
  );
  if (normalizedLocationValues === normalizeLocationValueList(ALL_AUSTRALIA_LOCATION_VALUES)) {
    return { tier: 'country', country: 'AU' };
  }
  if (normalizedLocationValues === normalizeLocationValueList(ALL_NEW_ZEALAND_LOCATION_VALUES)) {
    return { tier: 'country', country: 'NZ' };
  }

  const stateDisplay = generateLocationNameFromLocationString(locationValue) || locationValue;
  return { tier: 'state', state: stateDisplay };
};

/**
 * Generate location info
 * @param city
 * @param locationDisplay
 * @param country
 * @returns
 */
export const generateLocationInfo = (locationDisplay: string, country?: string, city?: string) => {
  const countryDetails = country ? ` ${country}` : '';
  if (city) {
    return `${city}, ${locationDisplay}${countryDetails}`;
  }
  return `${locationDisplay}${countryDetails}`;
};

/**
 *  Clean up search query data for job alerts use
 */

const jobAlertQueryParams = new Set([
  'grade',
  'speciality',
  'rootSpeciality',
  'subspeciality',
  'location',
  'workType',
  'startDate',
  'endDate',
  'keywords',
]);

export function convertJobSearchQueryToJobAlertQuery(query: JobSearchWebCriteria): IQuery {
  const newQuery = { ...query };

  for (const [key] of Object.entries(newQuery)) {
    if (!jobAlertQueryParams.has(key)) {
      delete newQuery[key];
    }
  }

  if (query.location) {
    newQuery.location = convertLocationsToValues(newQuery.location);
  }

  if (newQuery.grade === 'none') {
    delete newQuery.grade;
  }

  return newQuery as IQuery;
}

/**
 *  Convert Job Alert query params into displayable strings
 */

export function getJobAlertQueryDisplayValues(query: IQuery): string[] {
  const list: string[] = [];
  if (query?.keywords) {
    list.push(query.keywords);
  }

  if (query?.location) {
    const arrayLocations = convertLocationStringToLocationDisplayList(query.location);
    list.push(arrayLocations.join(', '));
  } else {
    list.push(ALL_LOCATIONS);
  }

  if (query?.grade) {
    list.push(query.grade);
  }

  if (query?.speciality) {
    list.push(query.speciality);
  }

  if (query?.rootSpeciality) {
    list.push(query.rootSpeciality);
  }

  if (query?.subspeciality) {
    list.push(query.subspeciality);
  }

  if (query?.workType) {
    list.push(query.workType);
  }

  if (query?.startDate || query?.endDate) {
    const dateTagFormat = 'DD/MM/YYYY';

    const endD = query?.endDate ? ` ~ ${dayjs(query?.endDate).format(dateTagFormat)}` : '';
    const startD = query?.startDate ? `${dayjs(query?.startDate).format(dateTagFormat)}` : 'NOW';

    list.push(`${startD}${endD}`);
  }

  return list;
}

// Removes 'amp;' from misencoded url keys that start with 'amp;'
const stripAmpFromKeys = (query: ParsedUrlQuery) => {
  const output: ParsedUrlQuery = {};
  for (const [key, value] of Object.entries(query)) {
    if (key.startsWith('amp;')) {
      const newKey = key.slice(4);
      output[newKey] = value;
    } else {
      output[key] = value;
    }
  }
  return output;
};

export const parseQueryForGetJobs = (query: ParsedUrlQuery) => {
  const convertedQuery = { ...stripAmpFromKeys(query) };
  // reduce noise from page parameter attacks
  if (convertedQuery.page && typeof convertedQuery.page === 'string') {
    const sanitizedPage = convertedQuery.page.replace(/\D/g, '');
    const parsedPage = parseInt(sanitizedPage, 10); // can return a NAN so check below
    convertedQuery.page = !isNaN(parsedPage) && sanitizedPage !== '' ? String(parsedPage) : '1';
  }

  if (convertedQuery.location) {
    convertedQuery.location = convertLocationsToValues(convertedQuery.location as string);
  }
  if (convertedQuery.startDate) {
    convertedQuery.startDate =
      parseAndFormatDate(convertedQuery.startDate as string, 'YYYY-MM-DD') ?? undefined;
  }
  if (convertedQuery.endDate) {
    convertedQuery.endDate =
      parseAndFormatDate(convertedQuery.endDate as string, 'YYYY-MM-DD') ?? undefined;
  }
  if (convertedQuery.rootSpeciality) {
    convertedQuery.speciality = convertedQuery.rootSpeciality;
    convertedQuery.rootSpeciality = undefined;
  }

  return convertedQuery;
};

export type JobsListingMetaTitleFilters = {
  count: number;
  grade?: string;
  speciality?: string;
  location?: string;
  workType?: string;
};

const JOBS_META_TITLE_SUFFIX = ' | Medrecruit';
const JOBS_META_TITLE_MAX_LENGTH = 60;

/**
 * Builds the jobs listing meta title from the active filter state, following the
 * formula: {Count} {Grade} {Speciality} Jobs{in Location?}{| WorkType?} | Medrecruit
 * Drops the job count if including it would exceed the 60 character limit.
 */
export const buildJobsListingMetaTitle = ({
  count,
  grade,
  speciality,
  location,
  workType,
}: JobsListingMetaTitleFilters): string => {
  const subject = [grade, speciality].filter(Boolean).join(' ');
  let titleWithoutCount = subject ? `${subject} Jobs` : 'Jobs';

  if (location) {
    titleWithoutCount += ` in ${location}`;
  }
  if (workType) {
    titleWithoutCount += ` | ${workType}`;
  }
  titleWithoutCount += JOBS_META_TITLE_SUFFIX;

  const titleWithCount = `${count} ${titleWithoutCount}`;

  return titleWithCount.length <= JOBS_META_TITLE_MAX_LENGTH ? titleWithCount : titleWithoutCount;
};

export type JobsListingMetaDescriptionFilters = {
  count: number;
  grade?: string;
  speciality?: string;
  location?: string;
  workType?: string;
};

const JOBS_META_DESCRIPTION_SUFFIX =
  '. Updated daily. Locum and permanent positions at leading hospitals. Register free.';
const JOBS_META_DESCRIPTION_MAX_LENGTH = 160;

/**
 * Builds the jobs listing meta description from the active filter state, following the
 * formula: Browse {Count} {WorkType?} {Speciality} jobs{for Grade?}{in Location?}. Updated
 * daily. Locum and permanent positions at leading hospitals. Register free.
 */
export const buildJobsListingMetaDescription = ({
  count,
  grade,
  speciality,
  location,
  workType,
}: JobsListingMetaDescriptionFilters): string => {
  const subject = [workType, speciality].filter(Boolean).join(' ');
  let description = subject ? `Browse ${count} ${subject} jobs` : `Browse ${count} jobs`;

  if (grade) {
    description += ` for ${grade}`;
  }
  if (location) {
    description += ` in ${location}`;
  }
  description += JOBS_META_DESCRIPTION_SUFFIX;

  return textlengthLimit(description, JOBS_META_DESCRIPTION_MAX_LENGTH);
};

export type JobsListingHeadingFilters = {
  grade?: string;
  speciality?: string;
  location?: string;
  state?: string;
};

export type JobsListingHeading = {
  gradeLine?: string;
  prefix: string;
  accent?: string;
};

/**
 * Builds the jobs listing page heading from the active filter state. The grade (if any)
 * is returned separately (as `gradeLine`) so it can be rendered on its own line in a
 * smaller regular weight, per the Figma design; the speciality/location line is the
 * larger accented heading, following the formula: {Speciality} jobs in {Location}.
 */
export const buildJobsListingHeading = ({
  grade,
  speciality,
  location,
  state,
}: JobsListingHeadingFilters): JobsListingHeading => {
  // Neither a Role filter (grade/speciality) is active - "Jobs" alone reads as too generic on a
  // site that's exclusively for doctors, so fall back to "Doctor jobs" instead. Once a grade is
  // selected it already anchors the heading via gradeLine, so bare "Jobs" reads fine there.
  const prefix = speciality ? `${speciality} jobs` : grade ? 'Jobs' : 'Doctor jobs';

  if (!location) {
    return { gradeLine: grade, prefix };
  }

  if (state) {
    const abbreviatedState = stateAbbreviations[state] || state;
    return { gradeLine: grade, prefix: `${prefix} in`, accent: `${location}, ${abbreviatedState}` };
  }

  return { gradeLine: grade, prefix: `${prefix} in`, accent: location };
};

/**
 * Builds the jobs listing page header's description copy, e.g. "Explore Specialist
 * Consultant opportunities in Australia at leading hospitals. Stay updated daily on
 * locum, and permanent positions to enhance your career." Falls back to "Australia"
 * when no location filter is active.
 */
export const buildJobsListingDescription = ({
  grade,
  location,
}: Pick<JobsListingHeadingFilters, 'grade' | 'location'>): string => {
  const subject = grade ? `${grade} ` : '';
  const place = location || 'Australia';

  return `Explore ${subject}opportunities in ${place} at leading hospitals. Stay updated daily on locum, and permanent positions to enhance your career.`;
};

export type FaqHeadingFilters = {
  grade?: string;
  speciality?: string;
  location?: string;
};

export type FaqHeading = {
  prefix: string;
  accent: string;
};

/**
 * Builds the FAQ section heading from the active grade/speciality/location context, e.g.
 * "Frequently asked questions about" + accented "Registrar jobs in Sydney".
 */
export const buildFaqHeading = ({ grade, speciality, location }: FaqHeadingFilters): FaqHeading => {
  const subject = [grade, speciality].filter(Boolean).join(' ') || 'doctor';
  const descriptor = `${subject} jobs`;

  return {
    prefix: 'Frequently asked questions about',
    accent: location ? `${descriptor} in ${location}` : descriptor,
  };
};

/**
 * Builds the FAQ section description copy, e.g. "Common questions from doctors searching
 * for Registrar roles in Sydney."
 */
export const buildFaqDescription = ({ grade, speciality, location }: FaqHeadingFilters): string => {
  const subject = [grade, speciality].filter(Boolean).join(' ');
  const roleLabel = subject ? `${subject} roles` : 'their next role';

  return `Common questions from doctors searching for ${roleLabel}${
    location ? ` in ${location}` : ''
  }.`;
};

/**
 * Builds the last breadcrumb crumb for the jobs listing page from the active filter
 * state, e.g. "Anaesthetics in Sydney".
 */
export const buildJobsListingBreadcrumbLabel = ({
  speciality,
  location,
}: Pick<JobsListingHeadingFilters, 'speciality' | 'location'>): string => {
  if (speciality && location) {
    return `${speciality} in ${location}`;
  }

  return speciality || location || 'Job search';
};

/**
 * Builds the Doctor Stories section description on the jobs listing page from the
 * active filter state, e.g. "Cardiology Registrar doctors we've placed across Sydney
 * — from first locum shift to permanent post." Falls back to generic "Doctors we've
 * placed..." copy when no grade/speciality/location filters are active, so the section
 * never claims a location or grade the visible page isn't actually filtered to.
 */
export const buildDoctorStoriesDescription = ({
  grade,
  speciality,
  location,
}: Pick<JobsListingHeadingFilters, 'grade' | 'speciality' | 'location'>): string => {
  const modifiers = [speciality, grade].filter(Boolean).join(' ');
  const subject = modifiers ? `${modifiers} doctors` : 'Doctors';
  const where = location ? ` across ${location}` : '';

  return `${subject} we've placed${where} — from first locum shift to permanent post.`;
};

const JOBS_COUNT_LABEL_ROUNDING_THRESHOLD = 10;

/**
 * Builds the job count stat label shown in the jobs listing page header, e.g. "30+ jobs found".
 * Counts below the rounding threshold are shown exactly; larger counts are rounded down
 * to the nearest 10 so the label doesn't need to change every time a single job is
 * added or removed.
 */
export const buildJobsListingCountLabel = (
  total: number,
  speciality?: string,
  grade?: string,
): string => {
  const subjectPrefix = speciality || grade;
  const subject = subjectPrefix ? `${subjectPrefix} jobs` : 'jobs';

  if (total < JOBS_COUNT_LABEL_ROUNDING_THRESHOLD) {
    return `${total} ${subjectPrefix ? subject : `job${total !== 1 ? 's' : ''}`} found`;
  }

  const roundedDown =
    Math.floor(total / JOBS_COUNT_LABEL_ROUNDING_THRESHOLD) * JOBS_COUNT_LABEL_ROUNDING_THRESHOLD;

  return `${roundedDown}+ ${subject} found`;
};

export const extractPerfectlyMatchingResults = (
  jobs: JobListPaginatedResults,
): JobListPaginatedResults => {
  const perfectlyMatchingJobs = jobs.data.filter(
    job => !job.matched || job.matched.partial === false,
  );

  const perfectJobResults: JobListPaginatedResults = {
    limit: jobs.limit,
    page: jobs.page,
    total: jobs.total,
    data: perfectlyMatchingJobs,
  };

  return perfectJobResults;
};

// Rendered similar-jobs cards are capped at this count (see JobListing.tsx) - trimming the list to
// this length here, rather than only at render time, keeps the getServerSideProps page props (and
// therefore the JSON-LD built from this same prop) from shipping jobs that never get shown.
export const MAX_SIMILAR_JOBS = 5;

// Query params can arrive as a string array (e.g. a repeated `?grade=` in the URL), while a job's
// grade is always a single display string - normalise here, once, rather than casting to `string`
// at each getServerSideProps call site, which would silently stop the grade-match sort below from
// ever matching.
export const getSearchedGrade = (grade: string | string[] | undefined): string | undefined =>
  Array.isArray(grade) ? grade[0] : grade;

export const extractSimilarJobs = (
  jobs: JobSearchResultDetail[],
  searchedGrade?: string,
): IJobDetail[] => {
  const similarJobs: JobSearchResultDetail[] = jobs.filter(job => {
    return job.matched && job.matched.partial === true;
  });

  if (!searchedGrade) {
    return similarJobs.slice(0, MAX_SIMILAR_JOBS);
  }

  // Jobs matching the searched grade are the closest match to what the user was looking for,
  // so surface them ahead of jobs that only matched on other criteria (speciality, keywords,
  // etc.) - this matters more now that the section is capped to a handful of cards. Array#sort
  // is stable, so relative order is otherwise preserved within each group.
  return [...similarJobs]
    .sort((a, b) => {
      const aMatchesGrade = a.grade === searchedGrade ? 0 : 1;
      const bMatchesGrade = b.grade === searchedGrade ? 0 : 1;
      return aMatchesGrade - bMatchesGrade;
    })
    .slice(0, MAX_SIMILAR_JOBS);
};

/**
 * A minimal per-job shape for the "Explore similar searches" section - just enough to
 * group and count jobs by location/speciality/grade/workType client-side, without shipping
 * the full IJobDetail payload (description, salary, etc.) to the browser. The underlying pool
 * is fetched unscoped (see getJobsForExploreSimilarSearches), so grade/workType/speciality are
 * carried here to be filtered client-side rather than baked into the fetch itself.
 */
export interface SimilarSearchJobSummary {
  location: string;
  locationDisplay?: string;
  speciality?: string;
  city?: string;
  country?: string;
  grade?: string;
  workType?: string;
}

export const toSimilarSearchJobSummaries = (jobs: IJobDetail[]): SimilarSearchJobSummary[] =>
  jobs.map(({ location, locationDisplay, speciality, city, country, grade, workType }) => ({
    location,
    // getServerSideProps props must be JSON-serializable - `undefined` values throw, so
    // optional fields are only included when present rather than set to `undefined`.
    ...(locationDisplay ? { locationDisplay } : {}),
    ...(speciality ? { speciality } : {}),
    ...(city ? { city } : {}),
    ...(country ? { country } : {}),
    ...(grade ? { grade } : {}),
    ...(workType ? { workType } : {}),
  }));

/**
 * Clears `speciality` on summaries whose canonical slug isn't a recognized root speciality
 * (e.g. subspecialities like "Cardiology"/"Addiction medicine" that need a parent root
 * segment - "medicine/cardiology" - which the "Explore similar searches" section's flat
 * grade/speciality links don't provide). Jobs tagged with a subspeciality still contribute
 * to the "By location" tab; they're just excluded from "By type" candidates so we never
 * link to a combination that resolves to zero results.
 */
export const filterCanonicalSpecialitySummaries = (
  summaries: SimilarSearchJobSummary[],
  knownSpecialitySlugs: Set<string>,
): SimilarSearchJobSummary[] =>
  summaries.map(({ speciality, ...rest }) => {
    const isCanonical = speciality && knownSpecialitySlugs.has(toCanonicalSlug(speciality));
    // getServerSideProps props must be JSON-serializable - omit the key entirely rather
    // than setting it to `undefined`, which throws.
    return isCanonical ? { ...rest, speciality } : rest;
  });

/**
 * Builds breadcrumb items for job listing pages with Schema.org microdata.
 * Breadcrumb structure: Medrecruit > Doctor Jobs > {Grade} > {Speciality} > {Location?} > {Work Type?}
 * Uses absolute URLs for SEO crawlability.
 * Accepts slug values to build proper URL paths while generating display labels.
 */
export const buildJobListingBreadcrumbs = (
  slugValues: {
    grade?: string;
    speciality?: string;
    rootSpeciality?: string;
    subspeciality?: string;
    location?: string;
    locationName?: string;
    workType?: string;
    locationSlug?: string;
    workTypeSlug?: string;
  },
  baseUrl: string,
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'Medrecruit',
      href: `${baseUrl}`,
    },
    {
      label: 'Doctor Jobs',
      href: `${baseUrl}/jobs/list`,
    },
  ];

  const pathSegments: string[] = [];

  // Add Grade if available
  if (slugValues.grade) {
    const gradeLabel =
      slugValues.grade === 'smo-moss' ? 'SMO/MOSS' : toDisplayValueFromSlug(slugValues.grade);
    pathSegments.push(slugValues.grade);

    breadcrumbs.push({
      label: gradeLabel,
      href: `${baseUrl}/jobs/listing/${pathSegments.join('/')}`,
    });
  }

  // Add Root Speciality if available
  const specialitySlug = slugValues.rootSpeciality || slugValues.speciality;
  if (specialitySlug) {
    const specialityLabel = toDisplayValueFromSlug(specialitySlug);
    pathSegments.push(specialitySlug);

    breadcrumbs.push({
      label: specialityLabel,
      href: `${baseUrl}/jobs/listing/${pathSegments.join('/')}`,
    });
  }

  // Add Sub Speciality if available
  if (slugValues.subspeciality) {
    const subspecialityLabel = toDisplayValueFromSlug(slugValues.subspeciality);
    pathSegments.push(slugValues.subspeciality);

    breadcrumbs.push({
      label: subspecialityLabel,
      href: `${baseUrl}/jobs/listing/${pathSegments.join('/')}`,
    });
  }

  // Add Location if available
  if (slugValues.locationSlug) {
    const locationLabel =
      slugValues.locationName || toDisplayValueFromSlug(slugValues.locationSlug);
    pathSegments.push(slugValues.locationSlug);

    breadcrumbs.push({
      label: locationLabel,
      href: `${baseUrl}/jobs/listing/${pathSegments.join('/')}`,
    });
  }

  // Add Work Type if available
  if (slugValues.workTypeSlug) {
    const workTypeLabel = slugValues.workType || toDisplayValueFromSlug(slugValues.workTypeSlug);
    pathSegments.push(slugValues.workTypeSlug);

    breadcrumbs.push({
      label: workTypeLabel,
      href: `${baseUrl}/jobs/listing/${pathSegments.join('/')}`,
    });
  }

  return breadcrumbs;
};

const ALL_GRADES_LABEL = 'All Grade';
const ALL_SPECIALITIES_LABEL = 'All Speciality';
const ALL_LOCATIONS_LABEL = 'All Locations';

export const getCanonicalOrigin = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.RECRUIT_WEB_URL || 'https://www.medrecruit.com.au';
};

interface BreadcrumbFilters {
  grade?: string;
  speciality?: string;
  location?: string;
  workType?: string;
}

export const buildJobsListingBreadcrumbItems = (
  filters: BreadcrumbFilters,
  origin: string,
): BreadcrumbItem[] => {
  const { grade, speciality, location, workType } = filters;

  const cleanOrigin = origin.replace(/\/$/, '');

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Medrecruit', href: `${cleanOrigin}` },
    { label: 'Doctor Jobs', href: `${cleanOrigin}/jobs/list` },
  ];

  const includeWorkType = Boolean(workType);
  const includeLocation = Boolean(location) || includeWorkType;
  const includeSpeciality = Boolean(speciality) || includeLocation;
  const includeGrade = Boolean(grade) || includeSpeciality;

  const pathSegments: string[] = [];
  // /jobs/listing is an optional catch-all, so a gap-filled "All Grade"/"All Speciality"/etc.
  // crumb (before any real filter is known) can link to the bare unfiltered listing page.
  const buildListingHref = () =>
    pathSegments.length > 0
      ? `${cleanOrigin}/jobs/listing/${pathSegments.join('/')}`
      : `${cleanOrigin}${PATH.JOBSLISTSEO}`;

  if (includeGrade) {
    const gradeSlug = grade ? toCanonicalSlug(grade) : '';
    if (gradeSlug) {
      pathSegments.push(gradeSlug);
    }
    breadcrumbs.push({
      label: grade || ALL_GRADES_LABEL,
      href: buildListingHref(),
    });
  }

  if (includeSpeciality) {
    const specialitySlug = speciality ? toCanonicalSlug(speciality) : '';
    if (specialitySlug) {
      pathSegments.push(specialitySlug);
    }
    breadcrumbs.push({
      label: speciality || ALL_SPECIALITIES_LABEL,
      href: buildListingHref(),
    });
  }

  if (includeLocation) {
    const locationSlug = location ? toCanonicalSlug(location) : '';
    if (locationSlug) {
      pathSegments.push(locationSlug);
    }
    breadcrumbs.push({
      label: location || ALL_LOCATIONS_LABEL,
      href: buildListingHref(),
    });
  }

  if (includeWorkType) {
    const workTypeSlug = workType ? toCanonicalSlug(workType) : '';
    if (workTypeSlug) {
      pathSegments.push(workTypeSlug);
    }
    breadcrumbs.push({
      label: workType || 'All Work Types',
      href: buildListingHref(),
    });
  }

  return breadcrumbs;
};
